import React, { useState, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { format, parseISO, addDays } from 'date-fns'
import Modal from 'components/utility/modals/GenericModal'

function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ''
}

async function fetchModalState(packageId) {
  const res = await fetch(`/package_hook/${packageId}/ny_1120b/extensions/modal_state`, {
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

async function postOneTimeExtension(packageId) {
  const res = await fetch(`/package_hook/${packageId}/ny_1120b/extensions/one_time`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
  })
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

async function postCustomExtension(packageId, desiredDate, reason) {
  const res = await fetch(`/package_hook/${packageId}/ny_1120b/extensions/custom_requests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify({ desired_deadline: format(desiredDate, 'yyyy-MM-dd'), reason }),
  })
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json()
}

export default function ExtendDeadlineModal({ packageId, devInitialState, customExtensionRequestPending }) {
  const [step, setStep] = useState('closed')
  const [selection, setSelection] = useState(null)
  const [modalState, setModalState] = useState(null)
  const [newDeadline, setNewDeadline] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState(null)
  const [customDesiredDate, setCustomDesiredDate] = useState(null)
  const [customReason, setCustomReason] = useState('')

  const isOpen = step !== 'closed'

  const handleOpen = useCallback(async () => {
    setStep('loading')
    setErrorMsg(null)
    try {
      const state = devInitialState ?? await fetchModalState(packageId)
      setModalState(state)
      setSelection(state.default_selected)
      setStep(prev => prev === 'loading' ? 'selection' : prev)
    } catch (e) {
      setErrorMsg('Unable to load extension options. Please try again.')
      setStep(prev => prev === 'loading' ? 'error' : prev)
    }
  }, [packageId, devInitialState])

  const handleClose = useCallback(() => {
    setStep('closed')
    setModalState(null)
    setSelection(null)
    setNewDeadline(null)
    setErrorMsg(null)
    setSubmitting(false)
    setCustomDesiredDate(null)
    setCustomReason('')
  }, [])

  const handleSubmitExtension = useCallback(async () => {
    setSubmitting(true)
    try {
      if (selection === 'custom') {
        await postCustomExtension(packageId, customDesiredDate, customReason)
      } else {
        const data = await postOneTimeExtension(packageId)
        setNewDeadline(data.current_deadline)
      }
      setStep('success')
    } catch (e) {
      setErrorMsg('Unable to submit extension request. Please try again.')
      setStep('error')
    } finally {
      setSubmitting(false)
    }
  }, [packageId, selection, customDesiredDate, customReason])

  const handleDismissSuccess = useCallback(() => {
    window.location.reload()
  }, [])

  const isNextDisabled = useMemo(() => {
    const selectedExtension = modalState?.[selection]
    if (!selectedExtension) return true
    if (!selectedExtension.eligible) return true

    if (selection === 'custom') {
      if (!customDesiredDate) return true
      const floor = parseISO(modalState.current_deadline)
      if (customDesiredDate <= floor) return true
      if (!customReason.trim()) return true
    }

    return false
  }, [modalState, selection, customDesiredDate, customReason])

  const oneTime = modalState?.one_time
  const custom  = modalState?.custom

  function oneTimeSubtext() {
    if (oneTime?.reason_disabled === 'already_used') {
      const formatted = oneTime.used_at ? format(parseISO(oneTime.used_at), 'MM/dd/yy') : ''
      return `Used ${formatted}`
    }
    return 'No approval required'
  }

  function customSubtext() {
    if (custom?.reason_disabled === 'one_time_required_first') return "Available after you've used the 14-day extension."
    return null
  }

  const TITLES = {
    success:      { custom: 'extension requested',       one_time: 'deadline extended' },
    confirmation: { custom: 'confirm extension request', one_time: 'confirm extension' },
  }
  const headerTitle = TITLES[step]?.[selection] ?? 'extend decision deadline'

  const header = (
    <div className='dome-color-dark-green dome-d-flex dome-flex-column dome-align-start dome-gap24'>
      <div className='dome-d-flex dome-flex-column dome-gap12'>
        <i className='fat fa-calendar-clock' style={{fontSize: '40px'}} />
        <div className="modal-title dome-text-w500 dome-h2">
          {headerTitle}
        </div>
      </div>
    </div>
  )

  function bodyContent() {
    if (step === 'loading') {
      return <div className="dome-d-flex dome-justify-center dome-p4"><i className="fal fa-spinner fa-spin" /></div>
    }

    if (step === 'error') {
      return (
        <div>
          <p className="dome-color-red-alert">{errorMsg}</p>
          <div className="dome-d-flex dome-justify-end">
            <button type="button" className="dome-btn dome-btn-link" onClick={handleClose}>close</button>
          </div>
        </div>
      )
    }

    function lastRequestInfo() {
      const lastRequest = custom?.last_request

      if (lastRequest?.status === 'denied') {
        return (
          <>
            <div className='dome-color-red-alert'>request denied on {format(parseISO(lastRequest.responded_at), 'MM/dd/yy')}.</div>
            <div>{lastRequest.response_reason}</div>
          </>
        )
      }

      if (lastRequest?.status === 'approved') {
        return <div className='dome-color-med-grey'>request approved on {format(parseISO(lastRequest.responded_at), 'MM/dd/yy')}.</div>
      }

      return <div className='dome-color-lite-grey'>request pending</div>
    }

    if (step === 'selection') {
      return (
        <div className='dome-d-flex dome-flex-column dome-gap24'>
          <div className='dome-d-flex dome-flex-column dome-gap18'>
            {modalState && step === 'selection' && (
              <div className="dome-color-dark-grey dome-p1 dome-text-left">
                <div>Your decision is due on <span className='dome-text-w500 dome-color-darkest-grey'>{format(parseISO(modalState.current_deadline), 'MM/dd/yyyy')}.</span></div>
                <div>Choose an extension option below:</div>
              </div>
            )}
            <div className="extend-deadline-modal__options dome-d-flex dome-flex-column dome-gap8">
              {/* 14-day option */}
              <label className={clsx('extend-deadline-modal__option dome-d-flex dome-align-start dome-gap12', !oneTime?.eligible && 'extend-deadline-modal__option--disabled')}>
                <input
                  type="radio"
                  name="extend_deadline_selection"
                  value="one_time"
                  checked={selection === 'one_time'}
                  disabled={!oneTime?.eligible}
                  onChange={() => setSelection('one_time')}
                />
                <div className={clsx(
                  "extend-deadline-modal__option-body dome-bg-fill dome-rounded-border dome-padding6-12 dome-text-left dome-flex-1",
                  selection === 'one_time' && 'selected-extension',
                  !oneTime?.eligible && 'extend-deadline-modal__option-body--disabled'
                )}>
                  <div className="dome-text-w500 dome-p2">14-day</div>
                  <div className={clsx("dome-p3", selection === 'one_time' ? 'dome-color-dark-grey' : 'dome-color-med-grey')}>{oneTimeSubtext()}</div>
                </div>
              </label>
              {/* Custom option */}
              <label className={clsx('extend-deadline-modal__option dome-d-flex dome-align-start dome-gap12', !custom?.eligible && 'extend-deadline-modal__option--disabled')}>
                <input
                  type="radio"
                  name="extend_deadline_selection"
                  value="custom"
                  checked={selection === 'custom'}
                  disabled={!custom?.eligible}
                  onChange={() => setSelection('custom')}
                />
                <div className='dome-d-flex dome-flex-column dome-gap6'>
                  <div className={clsx(
                    "extend-deadline-modal__option-body dome-bg-fill dome-rounded-border dome-padding6-12 dome-text-left dome-flex-1",
                    selection === 'custom' && 'selected-extension',
                    !custom?.eligible && 'extend-deadline-modal__option-body--disabled'
                  )}>
                    <div className="dome-text-w500 dome-p2">custom</div>
                    <div className={clsx("dome-p3", selection === 'custom' ? 'dome-color-dark-grey' : 'dome-color-med-grey')}>
                      {customSubtext() || (
                        <>
                          <span className='dome-color-aqua dome-text'>Note: </span>
                          Must be approved by Applicant or Applicant's Agent. {" "}
                          {selection === 'custom' && (
                            <div>
                              <span>Only one extension request can be active at a time. A new request will replace any previous request(s).</span>
                              <div className="extend-deadline-modal__custom-form dome-d-flex dome-flex-column dome-gap6">
                                <div className='dome-d-flex dome-flex-column dome-gap8'>
                                  <label className="dome-p2 dome-color-dark-grey">
                                    desired date*
                                  </label>
                                  <div className="extend-deadline-modal__date-wrapper">
                                    <input
                                      type="date"
                                      className="extend-deadline-modal__date-input dome-p3"
                                      value={customDesiredDate ? format(customDesiredDate, 'yyyy-MM-dd') : ''}
                                      min={format(addDays(parseISO(modalState.current_deadline), 1), 'yyyy-MM-dd')}
                                      onChange={e => setCustomDesiredDate(e.target.value ? parseISO(e.target.value) : null)}
                                    />
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none" className="extend-deadline-modal__date-icon">
                                      <g clipPath="url(#clip0_3822_1627)">
                                        <path d="M3.75 0C3.8875 0 4 0.1125 4 0.25V2H10V0.25C10 0.1125 10.1125 0 10.25 0C10.3875 0 10.5 0.1125 10.5 0.25V2H12C13.1031 2 14 2.89687 14 4V5.5V6V14C14 15.1031 13.1031 16 12 16H2C0.896875 16 0 15.1031 0 14V6V5.5V4C0 2.89687 0.896875 2 2 2H3.5V0.25C3.5 0.1125 3.6125 0 3.75 0ZM13.5 6H0.5V14C0.5 14.8281 1.17188 15.5 2 15.5H12C12.8281 15.5 13.5 14.8281 13.5 14V6ZM3.5 3.75V2.5H2C1.17188 2.5 0.5 3.17188 0.5 4V5.5H13.5V4C13.5 3.17188 12.8281 2.5 12 2.5H10.5V3.75C10.5 3.8875 10.3875 4 10.25 4C10.1125 4 10 3.8875 10 3.75V2.5H4V3.75C4 3.8875 3.8875 4 3.75 4C3.6125 4 3.5 3.8875 3.5 3.75Z" fill="#666666"/>
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_3822_1627">
                                          <rect width="14" height="16" fill="white"/>
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  </div>
                                </div>
                                <div className='dome-d-flex dome-flex-column dome-gap8'>
                                  <label className="dome-p2 dome-color-dark-grey">
                                    reason*
                                  </label>
                                  <textarea
                                    className="extend-deadline-modal__reason-textarea dome-p3"
                                    value={customReason}
                                    onChange={e => setCustomReason(e.target.value)}
                                    placeholder="value"
                                    rows={1}
                                    maxLength={250}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {custom?.last_request?.status && (
                    <div className="dome-p3 dome-color-dark-grey dome-d-flex dome-align-baseline dome-gap6">
                      <i className='fal fa-circle-exclamation' />
                      <div>
                        <div>An extension until {format(parseISO(custom?.last_request?.desired_deadline), "MM/dd/yy")} was requested on {format(parseISO(custom?.last_request?.requested_at), "MM/dd/yy")}.</div>
                        {lastRequestInfo()}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              
            </div>
          </div>

          <div className="dome-d-flex dome-justify-between">
            <button type="button" className="dome-btn dome-btn-link dome-btn-link-cancel" onClick={handleClose}>cancel</button>
            <button
              type="button"
              className={clsx("dome-btn dome-btn-base", isNextDisabled ? 'dome-btn-disabled' : 'dome-btn-aqua-stroke')}
              disabled={isNextDisabled}
              onClick={() => setStep('confirmation')}
            >
              next
            </button>
          </div>
        </div>
      )
    }

    if (step === 'confirmation') {
      if (selection === 'custom') {
        return (
          <div className='dome-d-flex dome-flex-column dome-gap24'>
            <div className='dome-d-flex dome-flex-column dome-gap24 dome-p1 dome-color-dark-grey dome-text-left'>
              <div className='dome-d-flex dome-flex-column dome-color-dark-grey'>
                <div>Current deadline: {format(parseISO(modalState.current_deadline), 'MM/dd/yy')}</div>
                <div className='dome-d-flex dome-align-center dome-gap12'>
                  <div>Desired deadline: {format(customDesiredDate, 'MM/dd/yy')}</div>
                  {modalState.custom.last_request?.requested_at && <div className='dome-padding3-6 dome-p3 dome-bg-accent dome-color-aqua dome-rounded-border'>Updated request</div>}
                </div>
                <div>Reason: {customReason}</div>
              </div>
              <div className='dome-disabled-border dome-border-top-none'></div>
              <div className='dome-d-flex dome-flex-column dome-gap6'>
                <div>We'll email the address(es) below to request this extension.</div>
                <div className='dome-color-aqua dome-text-w500'>{modalState.applicant_emails?.join(' ')}</div>
                <div>You'll receive an email with the decision. The deadline will be updated if the request is approved.</div>
              </div>
              <div><span className='dome-color-aqua'>Note:</span> Please wait for a response before relying on the proposed deadline.</div>
            </div>
            <div className="dome-d-flex dome-justify-between">
              <button type="button" className="dome-btn dome-btn-link dome-btn-link-cancel" onClick={handleClose} disabled={submitting}>cancel</button>
              <button
                type="button"
                className="dome-btn dome-btn-base dome-btn-dark-green"
                onClick={handleSubmitExtension}
                disabled={submitting}
              >
                {submitting ? <i className="fal fa-spinner fa-spin" /> : modalState.custom.last_request?.requested_at ? "update extension" : "request extension"}
              </button>
            </div>
          </div>
        )
      }

      return (
        <div className='dome-d-flex dome-flex-column dome-gap24'>
          <div className='dome-d-flex dome-flex-column dome-gap18 dome-p1 dome-color-dark-grey dome-text-left'>
            <div>This extension can only be used one time.</div>
            <div>Upon confirming, the Applicant or Applicant's agent will be notified of the new deadline via email.</div>
          </div>
          <div className="dome-d-flex dome-justify-between">
            <button type="button" className="dome-btn dome-btn-link dome-btn-link-cancel" onClick={handleClose} disabled={submitting}>cancel</button>
            <button
              type="button"
              className="dome-btn dome-btn-base dome-btn-dark-green"
              onClick={handleSubmitExtension}
              disabled={submitting}
            >
              {submitting ? <i className="fal fa-spinner fa-spin" /> : 'extend deadline'}
            </button>
          </div>
        </div>
      )
    }

    if (step === 'success') {
      if (selection === 'custom') {
        return (
          <div>
            <div className='dome-text-left dome-d-flex dome-flex-column dome-gap24'>
              <div>The address(es) will be alerted and asked to respond to this extension request.</div>
              <div className="dome-d-flex dome-justify-end">
                <button type="button" className="dome-btn dome-btn-base dome-btn-aqua-stroke" onClick={handleDismissSuccess}>OK</button>
              </div>
            </div>
          </div>
        )
      }

      return (
        <div>
          <div className='dome-text-left dome-d-flex dome-flex-column dome-gap24'>
            <div>Board decision is now due on <span className='dome-text-w500'>{newDeadline ? format(parseISO(newDeadline), 'MM/dd/yyyy') : ''}.</span></div>
            <div>All board members and Applicant or Applicant's agent will be notified of the new deadline.</div>
            <div className="dome-d-flex dome-justify-end">
              <button type="button" className="dome-btn dome-btn-base dome-btn-aqua-stroke" onClick={handleDismissSuccess}>OK</button>
            </div>
          </div>
        </div>
      )
    }

    return null
  }

  return (
    <>
      {customExtensionRequestPending && (
        <div className="dome-p2 dome-color-dark-grey">An extension is pending approval.</div>
      )}
      <button
        type="button"
        className="dome-btn dome-btn-link dome-btn-link-aqua"
        onClick={handleOpen}
      >
        <span>request new deadline</span>
        <i className='fal fa-arrow-right-long' />
      </button>

      <Modal
        open={isOpen}
        header={header}
        onClose={handleClose}
        className="extend-deadline-modal"
      >
        {bodyContent()}
      </Modal>
    </>
  )
}
